import { FC } from "react";
import { GridColDef } from "@mui/x-data-grid";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { format } from "date-fns";
import { ExportQuote } from "@/apis/bacchus/types";
import { formatNumber } from "@/utils/numberUtils";

function formatDate(dateString: string | undefined | null): string {
  if (!dateString) return "—";
  return format(new Date(dateString), "MMM d, yyyy");
}

function formatDateFull(dateString: string | undefined | null): string {
  if (!dateString) return "";
  return format(new Date(dateString), "MMM d, yyyy 'at' h:mm a");
}

interface QuoteActionsCellProps {
  quote: ExportQuote;
  onDeleteClick: (quote: ExportQuote) => void;
  onEditClick: (quote: ExportQuote) => void;
}

const QuoteActionsCell: FC<QuoteActionsCellProps> = ({
  quote,
  onDeleteClick,
  onEditClick,
}) => (
  <Stack direction="row" spacing={0.5}>
    {quote.permissions?.write && (
      <Tooltip title="Rename">
        <IconButton
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            onEditClick(quote);
          }}
          data-testid={`quote-row-edit-${quote.id}`}
        >
          <EditOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    )}
    {quote.permissions?.delete && (
      <Tooltip title="Delete">
        <IconButton
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            onDeleteClick(quote);
          }}
          data-testid={`quote-row-delete-${quote.id}`}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    )}
  </Stack>
);

export function getColumns(
  onDeleteClick: (quote: ExportQuote) => void,
  onEditClick: (quote: ExportQuote) => void
): GridColDef<ExportQuote>[] {
  return [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 200,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title={params.row.name} placement="bottom">
          <Typography
            data-testid={`quote-row-link-${params.row.id}`}
            variant="body2"
            noWrap
          >
            {params.row.name || "—"}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "previous_estimated",
      headerName: "Estimated credits",
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Typography
          variant="body2"
          data-testid={`quote-row-credits-${params.row.id}`}
        >
          {params.row.previous_estimated
            ? formatNumber(params.row.previous_estimated.credits_estimate)
            : "—"}
        </Typography>
      ),
    },
    {
      field: "estimated_at",
      headerName: "Estimated at",
      width: 150,
      sortable: false,
      renderCell: (params) => {
        const estimatedAt = params.row.previous_estimated?.estimated_at;
        return (
          <Tooltip title={formatDateFull(estimatedAt)} placement="top">
            <Typography variant="body2">{formatDate(estimatedAt)}</Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "created_by",
      headerName: "Created by",
      width: 130,
      sortable: false,
      renderCell: (params) =>
        // permissions.write is only ever true for the quote's creator
        params.row.permissions?.write ? (
          <Chip
            data-testid={`quote-row-created-by-chip-${params.row.id}`}
            size="small"
            variant="outlined"
            color="primary"
            label="Me"
          />
        ) : (
          <Typography
            variant="body2"
            data-testid={`quote-row-created-by-${params.row.id}`}
          >
            Teammate
          </Typography>
        ),
    },
    {
      field: "inserted_at",
      headerName: "Created",
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title={formatDateFull(params.row.inserted_at)} placement="top">
          <Typography variant="body2">
            {formatDate(params.row.inserted_at)}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <QuoteActionsCell
          quote={params.row}
          onDeleteClick={onDeleteClick}
          onEditClick={onEditClick}
        />
      ),
    },
  ];
}
